import { createContext, useContext } from 'react'
import { ApiClient, initApiClient } from '@frontend/lib/request'

import { InboundOutboundType } from '@shared/const'

import { BulkSlotsUpdate, SlotBulkUpdate } from '@shared-types/bulk_actions'
import {
  AssignShipmentToSlotUsingTokenBody,
  BookingApprovalStatusUpdatePayload,
  BookingCapacity,
  BookingSummary,
  FacilityBookingWithoutContents,
  FreeBookingSlotQuery,
  ListBookingCapacitiesFilters,
  ProvisionSlotsResult,
  Slot,
  SlotCapacity,
  SlotProvisioningSettings,
  SlotUpdatePayload,
  UISlotListFilterParams,
} from '@shared-types/calendar'
import { FreeBookingSlot } from '@shared-types/scheduling'
import {
  DeliveredShipmentsSummary,
  ListDeliveredShipmentsSummaries,
  Shipment,
} from '@shared-types/shipments'
import { BulkUpdateResult, Collection, Identifier } from '@shared-types/util'

export interface CalendarClient {
  update: (
    facilityId: Identifier,
    bookingId: Identifier,
    payload: SlotUpdatePayload
  ) => Promise<Slot>
  all: (
    facilityId: Identifier,
    filters: UISlotListFilterParams
  ) => Promise<Collection<BookingSummary>>
  bulkCreate: (
    facilityId: Identifier,
    payload: SlotProvisioningSettings
  ) => Promise<ProvisionSlotsResult>
  assignShipmentToSlotUsingToken: (
    slotId: string,
    payload: AssignShipmentToSlotUsingTokenBody
  ) => Promise<FacilityBookingWithoutContents>
  getFreeTimeSlots: (
    token: string,
    start_date: string,
    end_date: string,
    reserved: boolean,
    type: InboundOutboundType,
    truck_type?: ListBookingCapacitiesFilters['truck_type']
  ) => Promise<Collection<FreeBookingSlot>>
  getBookingDetails: (facilityId: Identifier, bookingId: Identifier) => Promise<Slot>
  searchUnscheduledShipments: (
    facilityId: Identifier,
    nid__search: string
  ) => Promise<Collection<Shipment>>
  updateBookingApprovalStatus: (
    facilityId: Identifier,
    bookingId: Identifier,
    payload: BookingApprovalStatusUpdatePayload
  ) => Promise<BookingApprovalStatusUpdatePayload>
  getBookingCapacities: (
    facilityId: Identifier,
    params: ListBookingCapacitiesFilters
  ) => Promise<Collection<BookingCapacity>>
  getSlotCapacities: (
    token: Identifier,
    params: ListBookingCapacitiesFilters
  ) => Promise<Collection<SlotCapacity>>
  cancelBooking: (token: Identifier) => Promise<{ nid: Identifier }>
  getDeliveredShipmentSummaries: (
    facilityId: Identifier,
    params: ListDeliveredShipmentsSummaries
  ) => Promise<Collection<DeliveredShipmentsSummary>>
  bulkUpdate: (
    facilityId: Identifier,
    payload: BulkSlotsUpdate
  ) => Promise<{
    results: BulkUpdateResult<SlotBulkUpdate>[]
  }>
}

class DefaultCalendarClient implements CalendarClient {
  client: ApiClient
  constructor() {
    this.client = initApiClient({})
  }

  async update(facilityId: Identifier, bookingId: Identifier, payload: SlotUpdatePayload) {
    return this.client
      .patch(`/calendar/facilities/${facilityId}/bookings/${bookingId}`, payload)
      .then(({ data }) => data)
  }

  async all(facilityId: Identifier, filters: UISlotListFilterParams = {}) {
    return this.client
      .get(`/calendar/facilities/${facilityId}/bookings`, { params: filters })
      .then(({ data }) => data)
  }

  async bulkCreate(facilityId: Identifier, payload: SlotProvisioningSettings) {
    return this.client
      .post(`/calendar/facilities/${facilityId}/slots`, payload)
      .then(({ data }) => data)
  }

  async assignShipmentToSlotUsingToken(
    slotId: string,
    payload: AssignShipmentToSlotUsingTokenBody
  ) {
    return this.client.put(`/calendar/booking_slots/${slotId}`, payload).then(({ data }) => data)
  }

  async getFreeTimeSlots(
    token: string,
    start_date: string,
    end_date: string,
    reserved = false,
    type: InboundOutboundType,
    truck_type?: FreeBookingSlotQuery['truck_type']
  ) {
    return this.client
      .get(`/calendar/new_bookings/free_time_slots/${token}`, {
        params: {
          start_date,
          end_date,
          reserved,
          type,
          truck_type,
        },
      })
      .then(({ data }) => data)
  }

  async getBookingDetails(facilityId: Identifier, bookingId: Identifier) {
    return this.client
      .get(`/calendar/facilities/${facilityId}/bookings/${bookingId}/details`)
      .then((res) => res.data as Slot)
  }

  async searchUnscheduledShipments(facilityId: Identifier, nid__search: string) {
    const params = {
      receiving_facility: facilityId,
      nid__search,
      state: 'ACTIVE',
      booking_id: 'null',
    }
    return this.client.get('/shipments', { params }).then(({ data }) => data)
  }

  async updateBookingApprovalStatus(
    facilityId: Identifier,
    bookingId: Identifier,
    payload: BookingApprovalStatusUpdatePayload
  ) {
    return this.client
      .put(`/calendar/facilities/${facilityId}/bookings/${bookingId}/approval_status`, payload)
      .then(({ data }) => data)
  }

  async getBookingCapacities(facilityId: Identifier, params: ListBookingCapacitiesFilters) {
    return this.client
      .get(`/calendar/facilities/${facilityId}/booking_capacities`, { params })
      .then(({ data }) => data)
  }

  async getSlotCapacities(token: string, params: ListBookingCapacitiesFilters) {
    return this.client
      .get(`/calendar/new_bookings/slot_capacities/${token}`, { params })
      .then(({ data }) => data)
  }

  async cancelBooking(token: string) {
    return this.client.delete(`/calendar/bookings/${token}`).then(({ data }) => data)
  }

  async getDeliveredShipmentSummaries(
    facilityId: Identifier,
    params: ListDeliveredShipmentsSummaries
  ) {
    return this.client
      .get(`/calendar/facilities/${facilityId}/delivered_shipments_summaries`, { params })
      .then(({ data }) => data)
  }

  async bulkUpdate(facilityId: Identifier, payload: BulkSlotsUpdate) {
    return this.client
      .patch(`calendar/facilities/${facilityId}/slots/bulk_update`, payload)
      .then(({ data }) => data)
  }
}

const CalendarClientContext = createContext<CalendarClient>(new DefaultCalendarClient())

export function useCalendarClient() {
  return useContext(CalendarClientContext)
}
